import { MenuItem, Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  createVenueMaster,
  updateVenueMaster,
  fetchDeckList,
} from "../../utils/ApiCalls/Mobile/VenueMaster";
import { getVessels } from "../../utils/ApiCalls/SpecialPage";
import { checkFields } from "../../utils/Validation/checkFields";
import { addlog } from "../../utils/ApiCalls/Logs";
import { v4 as uuidv4 } from "uuid";

import {
  languages,
  locationLabels,
  sideLabels,
  vesselIdMap,
  vesselDecks
} from "../../constants/constant.config";
import VenueDataBlock from "./VenueDatablock";
import { LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";

const VenueMasterInput = ({ venues }) => {
  const [name, setName] = useState("");
  const [simplifiedChineseName, setSimplifiedChineseName] = useState("");
  const [traditionalChineseName, setTraditionalChineseName] = useState("");
  const [vesselList, setVesselList] = useState([]);
  const [vesselId, setVesselId] = useState(0);

  const [venueData, setVenueData] = useState([
    {
      id: uuidv4(),
      extensionNumber: null,
      deckNumber: null,
      locationEN: null,
      locationSC: null,
      locationTC: null,
      sideEN: null,
      sideSC: null,
      sideTC: null,
    },
  ]);

  const [isActive, setIsActive] = useState(true);
  const [decks, setDecks] = useState([]);
  const [{ userDetails }, dispatch] = useDataLayerValue();
    const location = useLocation();
  


  const [errors, setErrors] = React.useState({
    name: false,
    simplifiedChineseName: false,
    traditionalChineseName: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const vessels = await getVessels();
        setVesselList(vessels);
        if (venues?.vessel_id) {
          setVesselId(venues?.vessel_id);
        } else if (vessels[0]?.id) setVesselId(vessels[0].id);
      } catch (error) {
        console.error("Error fetching vessels:", error);
      }
    };
    fetchVessels();
  }, []);

  useEffect(() => {
    if (!vesselId) return;
    const fetchDecks = async () => {
      try {
        const deckList = await fetchDeckList(vesselId);
        setDecks(vesselDecks[vesselId]);
      } catch (error) {
        console.error("Error fetching decks:", error);
      }
    };
    fetchDecks();
  }, [vesselId]);

  useEffect(() => {
    if (venues) {
      setName(venues?.name || "");
      setSimplifiedChineseName(venues?.name_simplified_chinese || "");
      setTraditionalChineseName(venues?.name_traditional_chinese || "");
      setIsActive(venues?.is_active ?? true);
      setVesselId(venues?.vessel_id || 0);

      const mappedData = (venues?.data || []).map((venue) => ({
        id: uuidv4(),
        extensionNumber: venue?.extension || "",
        deckNumber: venue?.deck_number,
        locationEN: venue?.location || "",
        locationSC: venue?.location_simplified_chinese || "",
        locationTC: venue?.location_traditional_chinese || "",
        sideEN: venue?.side || "",
        sideSC: venue?.side_simplified_chinese || "",
        sideTC: venue?.side_traditional_chinese || "",
        refinedTags: venue?.refined_tags || "",
      }));

      setVenueData(
        mappedData.length
          ? mappedData
          : [
            {
              id: uuidv4(),
              extensionNumber: "",
              deckNumber: null,
              locationEN: "",
              locationSC: "",
              locationTC: "",
              sideEN: "",
              sideSC: "",
              sideTC: "",
            },
          ]
      );
    }
  }, [venues]);


  const handleCheckboxChange = () => setIsActive(!isActive);

  const handleAddDeck = () => {
    setVenueData([
      ...venueData,
      {
        id: uuidv4(),
        extensionNumber: "",
        deckNumber: null,
        locationEN: "",
        locationSC: "",
        locationTC: "",
        sideEN: "",
        sideSC: "",
        sideTC: "",
      },
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;
    const newErrors = { ...errors };

    if (!name.trim()) {
      newErrors.name = true;
      hasError = true;
    }
    if (!simplifiedChineseName.trim()) {
      newErrors.simplifiedChineseName = true;
      hasError = true;
    }
    if (!traditionalChineseName.trim()) {
      newErrors.traditionalChineseName = true;
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    dispatch({ type: "SET_LOADING", loading: true });
    const safeLookup = (obj, value) => obj[value] || value || "";

    const dataValues = {
      ship: vesselIdMap[vesselId],
      name,
      name_traditional_chinese: traditionalChineseName,
      name_simplified_chinese: simplifiedChineseName,
      vessel_id: vesselId,
      is_active: isActive ? 1 : 0,
      data: venueData.map((venue) => ({
        deck_number: venue?.deckNumber || null,
        extension: venue?.extensionNumber || "",
        location: safeLookup(locationLabels.EN, venue?.locationEN),
        location_simplified_chinese: safeLookup(locationLabels.SC, venue?.locationSC),
        location_traditional_chinese: safeLookup(locationLabels.TC, venue?.locationTC),
        side: safeLookup(sideLabels.EN, venue?.sideEN),
        side_simplified_chinese: safeLookup(sideLabels.SC, venue?.sideSC),
        side_traditional_chinese: safeLookup(sideLabels.TC, venue?.sideTC),
        refined_tags: Array.isArray(venue?.refinedTags)
          ? venue.refinedTags.join(",")
          : venue?.refinedTags || "",
      })),
    };

    let data;
    if (venues?.id) {
      data = await updateVenueMaster(venues?.id, vesselId, dataValues);
    } else {
      data = await createVenueMaster(dataValues);
    }

    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      dispatch({ type: "SET_LOADING", loading: false });
      return;
    }

    dispatch({ type: "SET_LOADING", loading: false });

    addlog(
      venues?.id ? LOG_ACTIONS.UPDATED : LOG_ACTIONS.CREATED,
      LOG_TYPES.Venues,
      { id: venues?.id || data?.id, title: `${name} Venue, ${vesselList.find(v => v.id === dataValues.vessel_id)?.name}`  },
      userDetails.id
    );

    const redirectSearch = location.search ? `${location.search}` : "";
    navigate(`/shore/mobile-masters/venue${redirectSearch}`);

    if (venues?.id) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: "Values updated", type: "success" },
      });
    }
  };

  return (
    <section className="content">
      <div className="container-fluid">
        <div className="card">
          <form onSubmit={handleSubmit} className="input_form">
            <div className="card-body">
              <div className="row">
                {[
                 {
                   label: "English Venue Name",
                   value: name,
                   setValue: setName,
                   errorKey: "name",
                 },
                  {
                    label: "Simplified Chinese Venue Name",
                    value: simplifiedChineseName,
                    setValue: setSimplifiedChineseName,
                    errorKey: "simplifiedChineseName",
                  },
                  {
                    label: "Traditional Chinese Venue Name",
                    value: traditionalChineseName,
                    setValue: setTraditionalChineseName,
                    errorKey: "traditionalChineseName",
                  },
                ].map((field, idx) => (
                  <div key={idx} className="col-4">
                    <div className="fieldrow mb-0">
                      <label className="f_label">{field.label} <span style={{color: 'red'}}>*</span></label>
                      <input
                        className="f_input"
                        type="text"
                        value={field.value}
                        onChange={(e) => {
                          field.setValue(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            [field.errorKey]: false,
                          }));
                        }}
                        style={{
                          border: errors[field.errorKey]
                            ? "1px solid red"
                            : "1px solid #ccc",
                        }}
                      />
                    </div>
                    {errors[field.errorKey] && (
                      <span style={{ color: "red", fontSize: "12px" }}>
                        {`${field.label} is required`}
                      </span>
                    )}
                  </div>
                ))}

                <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                  <div className="fieldrow">
                    <label className="f_label">
                      Vessels <span style={{ color: "red" }}>*</span>
                    </label>
                    <Select
                      style={{ width: "100%", height: "45px" }}
                      value={vesselId}
                      onChange={(e) => setVesselId(e.target.value)}
                    >
                      {vesselList.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          <div dangerouslySetInnerHTML={{ __html: v.name }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="w-100" />
                {venueData.map((item, index) => {
                  return (
                    <VenueDataBlock
                      key={item.id}
                      item={item}
                      index={index}
                      languages={languages}
                      decks={decks}
                      locationLabels={locationLabels}
                      sideLabels={sideLabels}
                      showDelete={venueData.length > 1}
                      onChange={(i, field, value) => {
                        const newData = [...venueData];
                        newData[i][field] = value;
                        setVenueData(newData);
                      }}
                      onDelete={(i) =>
                        setVenueData(venueData.filter((_, idx) => idx !== i))
                      }
                    />
                  );
                })}

                <div className="card-footer text-right">
                  <button
                    type="button"
                    className="btn main_submit"
                    onClick={handleAddDeck}
                  >
                    + Add Deck
                  </button>
                </div>

                <div className="col-12">
                  <div className="iti_inner">
                    <div className="icheck-primary d-inline">
                      <input
                        type="checkbox"
                        id="customCheckbox4"
                        name="r10"
                        checked={isActive}
                        onChange={handleCheckboxChange}
                      />
                      <label htmlFor="customCheckbox4">
                        {isActive ? (
                          <p>
                            <span style={{ fontWeight: 600 }}>Active</span>
                          </p>
                        ) : (
                          <p>
                            <span style={{ fontWeight: 200 }}>Active</span>
                          </p>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer text-right">
              <button className="btn main_submit">
                {venues?.id ? "Update" : "Add"} Venue
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default VenueMasterInput;
